/**
 * Integration logo helpers and registry.
 * Uses local /logos/ files where available, Composio CDN for the rest.
 * Provides a canonical list of integrations referenced by task templates and onboarding.
 */

export interface IntegrationRef {
	id: string;
	name: string;
	logoKey: string;
}

/** Build a logo URL — local paths (starting with /) served directly, others via Composio CDN */
export function getLogoUrl(logoKey: string): string {
	if (logoKey.startsWith("/")) return logoKey;
	return `https://logos.composio.dev/api/${logoKey}`;
}

/**
 * Canonical integration registry keyed by id.
 * logoKey is either a local path (e.g. "/logos/sentry.svg") or a Composio CDN key.
 */
export const INTEGRATIONS: Record<string, IntegrationRef> = {
	// Communication & Email
	gmail: { id: "gmail", name: "Gmail", logoKey: "gmail" },
	slack: { id: "slack", name: "Slack", logoKey: "slack" },
	slackbot: { id: "slackbot", name: "Slackbot", logoKey: "slack" },
	discord: { id: "discord", name: "Discord", logoKey: "discord" },
	"microsoft-teams": { id: "microsoft-teams", name: "Microsoft Teams", logoKey: "microsoft_teams" },
	whatsapp: { id: "whatsapp", name: "WhatsApp", logoKey: "whatsapp" },
	outlook: { id: "outlook", name: "Outlook", logoKey: "outlook" },
	// Productivity & Docs
	"google-drive": { id: "google-drive", name: "Google Drive", logoKey: "googledrive" },
	"google-docs": { id: "google-docs", name: "Google Docs", logoKey: "googledocs" },
	"google-sheets": { id: "google-sheets", name: "Google Sheets", logoKey: "googlesheets" },
	"google-calendar": { id: "google-calendar", name: "Google Calendar", logoKey: "googlecalendar" },
	"google-meet": { id: "google-meet", name: "Google Meet", logoKey: "googlemeet" },
	"google-tasks": { id: "google-tasks", name: "Google Tasks", logoKey: "googletasks" },
	"google-slides": { id: "google-slides", name: "Google Slides", logoKey: "googleslides" },
	"google-super": { id: "google-super", name: "Google Super", logoKey: "googlesuper" },
	notion: { id: "notion", name: "Notion", logoKey: "notion" },
	airtable: { id: "airtable", name: "Airtable", logoKey: "airtable" },
	confluence: { id: "confluence", name: "Confluence", logoKey: "confluence" },
	dropbox: { id: "dropbox", name: "Dropbox", logoKey: "dropbox" },
	box: { id: "box", name: "Box", logoKey: "box" },
	coda: { id: "coda", name: "Coda", logoKey: "coda" },
	miro: { id: "miro", name: "Miro", logoKey: "miro" },
	todoist: { id: "todoist", name: "Todoist", logoKey: "todoist" },
	// Design & Creative
	figma: { id: "figma", name: "Figma", logoKey: "figma" },
	canva: { id: "canva", name: "Canva", logoKey: "canva" },
	adobe: { id: "adobe", name: "Adobe", logoKey: "adobe" },
	// Project Management
	asana: { id: "asana", name: "Asana", logoKey: "asana" },
	jira: { id: "jira", name: "Jira", logoKey: "jira" },
	linear: { id: "linear", name: "Linear", logoKey: "/logos/linear.png" },
	monday: { id: "monday", name: "Monday", logoKey: "monday" },
	trello: { id: "trello", name: "Trello", logoKey: "trello" },
	clickup: { id: "clickup", name: "ClickUp", logoKey: "clickup" },
	wrike: { id: "wrike", name: "Wrike", logoKey: "wrike" },
	shortcut: { id: "shortcut", name: "Shortcut", logoKey: "shortcut" },
	// Developer Tools
	github: { id: "github", name: "GitHub", logoKey: "github" },
	gitlab: { id: "gitlab", name: "GitLab", logoKey: "gitlab" },
	bitbucket: { id: "bitbucket", name: "Bitbucket", logoKey: "bitbucket" },
	sentry: { id: "sentry", name: "Sentry", logoKey: "/logos/sentry.svg" },
	supabase: { id: "supabase", name: "Supabase", logoKey: "supabase" },
	pagerduty: { id: "pagerduty", name: "PagerDuty", logoKey: "pagerduty" },
	datadog: { id: "datadog", name: "Datadog", logoKey: "datadog" },
	"aws-cloudwatch": {
		id: "aws-cloudwatch",
		name: "AWS CloudWatch",
		logoKey: "/logos/CloudWatch.svg",
	},
	"aws-knowledge": { id: "aws-knowledge", name: "AWS", logoKey: "/logos/aws.svg" },
	azure: { id: "azure", name: "Azure", logoKey: "/logos/azure.png" },
	gcloud: { id: "gcloud", name: "Google Cloud", logoKey: "/logos/google_cloud.png" },
	// Analytics
	posthog: { id: "posthog", name: "PostHog", logoKey: "/logos/posthog.svg" },
	mixpanel: { id: "mixpanel", name: "Mixpanel", logoKey: "mixpanel" },
	amplitude: { id: "amplitude", name: "Amplitude", logoKey: "amplitude" },
	"google-analytics": {
		id: "google-analytics",
		name: "Google Analytics",
		logoKey: "google_analytics",
	},
	// CRM & Sales
	hubspot: { id: "hubspot", name: "HubSpot", logoKey: "hubspot" },
	salesforce: { id: "salesforce", name: "Salesforce", logoKey: "salesforce" },
	attio: { id: "attio", name: "Attio", logoKey: "attio" },
	affinity: { id: "affinity", name: "Affinity", logoKey: "affinity" },
	pipedrive: { id: "pipedrive", name: "Pipedrive", logoKey: "/logos/Pipedrive.png" },
	apollo: { id: "apollo", name: "Apollo", logoKey: "/logos/apollo.jpg" },
	"close-crm": { id: "close-crm", name: "Close CRM", logoKey: "/logos/close.svg" },
	zoho: { id: "zoho", name: "Zoho", logoKey: "zoho" },
	dynamics365: { id: "dynamics365", name: "Dynamics 365", logoKey: "dynamics365" },
	gong: { id: "gong", name: "Gong", logoKey: "gong" },
	// Marketing & Ads
	"google-ads": { id: "google-ads", name: "Google Ads", logoKey: "/logos/google-ads.png" },
	"meta-ads": { id: "meta-ads", name: "Meta Ads", logoKey: "metaads" },
	"google-search-console": {
		id: "google-search-console",
		name: "Google Search Console",
		logoKey: "/logos/logo_search_console.png",
	},
	semrush: { id: "semrush", name: "Semrush", logoKey: "semrush" },
	ahrefs: { id: "ahrefs", name: "Ahrefs", logoKey: "/logos/ahrefs.svg" },
	mailchimp: { id: "mailchimp", name: "Mailchimp", logoKey: "mailchimp" },
	brevo: { id: "brevo", name: "Brevo", logoKey: "/logos/brevo.svg" },
	klaviyo: { id: "klaviyo", name: "Klaviyo", logoKey: "klaviyo" },
	sendgrid: { id: "sendgrid", name: "SendGrid", logoKey: "sendgrid" },
	// Scheduling
	cal: { id: "cal", name: "Cal", logoKey: "/logos/cal.svg" },
	calendly: { id: "calendly", name: "Calendly", logoKey: "calendly" },
	// Search & Scraping
	tavily: { id: "tavily", name: "Tavily", logoKey: "/logos/tavily.png" },
	firecrawl: { id: "firecrawl", name: "Firecrawl", logoKey: "/logos/firecrawl.png" },
	// Automation
	zapier: { id: "zapier", name: "Zapier", logoKey: "/logos/zapier.svg" },
	// Ads
	adrapid: { id: "adrapid", name: "Adrapid", logoKey: "/logos/adrapid.webp" },
	// Cloud
	digitalocean: { id: "digitalocean", name: "DigitalOcean", logoKey: "/logos/digitalocean.png" },
	// E-commerce & Payments
	shopify: { id: "shopify", name: "Shopify", logoKey: "shopify" },
	stripe: { id: "stripe", name: "Stripe", logoKey: "/logos/stripe.png" },
	amazon: { id: "amazon", name: "Amazon", logoKey: "amazon" },
	// Social Media
	linkedin: { id: "linkedin", name: "LinkedIn", logoKey: "linkedin" },
	twitter: { id: "twitter", name: "Twitter", logoKey: "twitter" },
	reddit: { id: "reddit", name: "Reddit", logoKey: "reddit" },
	youtube: { id: "youtube", name: "YouTube", logoKey: "youtube" },
	facebook: { id: "facebook", name: "Facebook", logoKey: "facebook" },
	// Events
	eventbrite: { id: "eventbrite", name: "Eventbrite", logoKey: "eventbrite" },
	// Customer Support
	zendesk: { id: "zendesk", name: "Zendesk", logoKey: "zendesk" },
	freshdesk: { id: "freshdesk", name: "Freshdesk", logoKey: "freshdesk" },
	intercom: { id: "intercom", name: "Intercom", logoKey: "intercom" },
	// Meetings
	zoom: { id: "zoom", name: "Zoom", logoKey: "zoom" },
	// Finance & Accounting
	quickbooks: { id: "quickbooks", name: "QuickBooks", logoKey: "quickbooks" },
	xero: { id: "xero", name: "Xero", logoKey: "xero" },
	brex: { id: "brex", name: "Brex", logoKey: "brex" },
	// Documents & Signing
	docusign: { id: "docusign", name: "DocuSign", logoKey: "docusign" },
	pandadoc: { id: "pandadoc", name: "PandaDoc", logoKey: "pandadoc" },
	// HR & Recruiting
	bamboohr: { id: "bamboohr", name: "BambooHR", logoKey: "bamboohr" },
	lever: { id: "lever", name: "Lever", logoKey: "lever" },
	// AI & Media
	heygen: { id: "heygen", name: "HeyGen", logoKey: "heygen" },
	nia: { id: "nia", name: "Nia", logoKey: "/logos/nia.webp" },
	// Data & Warehousing
	snowflake: { id: "snowflake", name: "Snowflake", logoKey: "snowflake" },
	"google-bigquery": { id: "google-bigquery", name: "Google BigQuery", logoKey: "googlebigquery" },
	// Website & CMS
	webflow: { id: "webflow", name: "Webflow", logoKey: "webflow" },
	contentful: { id: "contentful", name: "Contentful", logoKey: "contentful" },
	// Scheduling
	calcom: { id: "calcom", name: "Cal.com", logoKey: "/logos/cal.svg" },
};
